import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AddBox, Close, InfoOutlined } from '@mui/icons-material';
import { ModalGeneral, Tabla } from 'ui-component';
import headers from './components/headers';
import MainCard from 'ui-component/cards/MainCard';
import ServiciosCalificacion from './components/ServiciosCalificacion';
import {
    Button,
    Grid,
    Tab,
    Tabs,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Chip,
    Stack,
    Tooltip,
    IconButton,
    Typography,
    Snackbar,
    Alert
} from '@mui/material';
import CursosCalificacionTree from './components/CursosTree';
import ModalEvidenciaColaborador from './components/ModalEvidenciaColaborador';
import { makeStyles } from '@mui/styles';
import { useLogin } from 'Context/ContextLogin';

const Calificaciones = ({ permisos }) => {
    const useStyles = makeStyles((theme) => ({
        btnAceptar: {
            color: theme.palette.btn.green,
            '&:hover': {
                backgroundColor: theme.palette.btn.green,
                color: theme.palette.btn.white
            }
        },
        btnCancelar: {
            color: theme.palette.btn.red,
            '&:hover': {
                backgroundColor: theme.palette.btn.red,
                color: theme.palette.btn.white
            }
        }
    }));
    const {
        usuario: { areas }
    } = useLogin();
    const serviciosCalificacion = ServiciosCalificacion();
    // States
    const [grupo, setGrupo] = useState({ opcion: areas[0]?.idarea || '', data: [] });
    const [areasActual, setAreasActual] = useState([]);
    const classes = useStyles();
    const [modal, setModal] = useState({ open: false, props: null, componente: null });

    const [cursos, setCursos] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [filtros, setFiltros] = useState({ idCurso: '', idGrupo: '' });
    const [filtrosAplicados, setFiltrosAplicados] = useState([]);
    const [mensaje, setMensaje] = useState({ open: false, value: '' });

    const hayFiltrosSeleccionados = !!filtros.idCurso || !!filtros.idGrupo;

    const filtrosCursoGrupoAplicados =
        filtrosAplicados.some((f) => f.label.startsWith('Curso:')) && filtrosAplicados.some((f) => f.label.startsWith('Grupo:'));

    const cerrarMensaje = () => setMensaje({ ...mensaje, open: false });

    const verCalificacionCurso = (row) => {
        serviciosCalificacion.getTrainerCalificacion(
            (data) => {
                setModal({
                    open: true,
                    titulo: `Calificación por curso para ${row.nombre_persona}`,
                    props: { colaborador: { ...row, ...data, permisos } },
                    componente: CursosCalificacionTree
                });
            },
            { idcolaborador: row.idColaborador }
        );
    };

    const verEvidencia = (row) => {
        setModal({
            open: true,
            titulo: `Evidencia de ${row.nombre_persona}`,
            props: {
                colaborador: row,
                idCurso: filtros.idCurso,
                nombreCurso: row.nombreCurso,
                nombreGrupo: row.nombreGrupo
            },
            componente: ModalEvidenciaColaborador
        });
    };

    const cerrarModal = () => {
        setModal({
            ...modal,
            open: false
        });
    };

    const clickCambioTipoPuesto = (event, value) => {
        setGrupo({ ...grupo, opcion: value });
        serviciosCalificacion.getTrainerCursosPuesto(
            (data) => {
                const colaboradoresConNA = (data.colaboradores || []).map((row) => ({
                    ...row,
                    nombreCurso: row.nombreCurso || 'N/A',
                    nombreGrupo: row.nombreGrupo || 'N/A'
                }));
                setGrupo({ opcion: value, data: colaboradoresConNA });
            },
            { opcion: value, idCurso: filtros.idCurso, idGrupo: filtros.idGrupo }
        );
    };

    const handleCambioCurso = (event) => {
        const idCurso = event.target.value;
        setFiltros({ idCurso, idGrupo: '' });
        setGrupos([]);
        if (idCurso) {
            serviciosCalificacion.getGruposPorCurso((data) => setGrupos(data.grupos || []), { idCurso });
        }
    };

    const handleCambioGrupo = (event) => {
        setFiltros((prev) => ({ ...prev, idGrupo: event.target.value }));
    };

    const ejecutarBusqueda = (idCurso, idGrupo) => {
        const opcionActual = grupo.opcion != '' ? grupo.opcion : areasActual[0]?.idarea;
        serviciosCalificacion.getTrainerCursosPuesto(
            (data) => {
                const colaboradoresConNA = (data.colaboradores || []).map((row, idx) => ({
                    ...row,
                    nombreCurso: row.nombreCurso || 'N/A',
                    nombreGrupo: row.nombreGrupo || 'N/A',
                    idcolaborador: idCurso ? `${row.idColaborador}_${row.nombreGrupo}_${idx}` : row.idColaborador
                }));

                setGrupo({ opcion: opcionActual, data: colaboradoresConNA });

                const chips = [];
                const cursoSel = cursos.find((c) => c.idCurso == idCurso);
                const grupoSel = grupos.find((g) => g.idGrupo == idGrupo);
                if (cursoSel) chips.push({ label: `Curso: ${cursoSel.titulo}` });
                if (grupoSel) chips.push({ label: `Grupo: ${grupoSel.nombre}` });
                setFiltrosAplicados(chips);
            },
            { opcion: opcionActual, idCurso, idGrupo }
        );
    };

    const buscar = () => {
        ejecutarBusqueda(filtros.idCurso, filtros.idGrupo);
    };

    const limpiarFiltros = () => {
        setFiltros({ idCurso: '', idGrupo: '' });
        setGrupos([]);
        setFiltrosAplicados([]);
        ejecutarBusqueda('', '');
    };

    // Config
    const acciones = [];
    const accionesFila = [
        {
            label: 'Calificación por curso',
            onClick: verCalificacionCurso,
            enabled: true
        },
        {
            label: 'Evidencia',
            onClick: verEvidencia,
            enabled: filtrosCursoGrupoAplicados,
            disabledTooltip: 'La opción de Evidencia está disponible al aplicar los filtros de búsqueda (Curso y Grupo).'
        }
    ];

    useEffect(() => {
        serviciosCalificacion.getCursosActivos((data) => {
            setCursos(data.cursos || []);
        });
    }, []);

    useEffect(() => {
        if (areas?.length) {
            let arrNuevo = [];
            areas.forEach((element) => {
                switch (Number(element.idarea)) {
                    case 1:
                        if (permisos.verAdmin && permisos.verAdmin == true) {
                            arrNuevo.push(element);
                        }
                        break;
                    case 2:
                        if (permisos.verOpe && permisos.verOpe == true) {
                            arrNuevo.push(element);
                        }
                        break;
                    case 3:
                        if (permisos.verVen && permisos.verVen == true) {
                            arrNuevo.push(element);
                        }
                        break;
                    case 4:
                        if (permisos.verOoam && permisos.verOoam == true) {
                            arrNuevo.push(element);
                        }
                        break;
                }
            });
            setAreasActual([...arrNuevo]);
            if (arrNuevo.length > 0) {
                setGrupo({ ...grupo, opcion: arrNuevo[0].idarea });
                clickCambioTipoPuesto({}, arrNuevo[0].idarea);
            }
        }
    }, [areas]);

    return (
        <MainCard
            title={
                <Grid container alignItems="center" spacing={2}>
                    <Grid item>Calificaciones</Grid>
                    {areasActual.length > 0 && (
                        <Grid item>
                            <Tabs
                                value={grupo.opcion != '' ? grupo.opcion : areasActual[0].idarea}
                                onChange={clickCambioTipoPuesto}
                                textColor="inherit"
                                aria-label="basic tabs example"
                            >
                                {areasActual.map((area) => (
                                    <Tab label={area.narea} value={area.idarea} />
                                ))}
                            </Tabs>
                        </Grid>
                    )}
                </Grid>
            }
        >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="body2" color="textSecondary">
                    Filtros: selecciona un curso para cargar y habilitar la búsqueda por grupos disponibles.
                </Typography>
                <Tooltip
                    title="Las columnas Curso y Grupo, y la opción de Evidencia, solo muestran información cuando se aplica el filtro de Curso y Grupo. Sin filtros, se mostrará N/A."
                    arrow
                >
                    <InfoOutlined fontSize="small" style={{ cursor: 'help', opacity: 0.7 }} />
                </Tooltip>
            </Stack>

            <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="lbl-curso">Curso</InputLabel>
                        <Select labelId="lbl-curso" label="Curso" value={filtros.idCurso} onChange={handleCambioCurso}>
                            <MenuItem value="">Todos</MenuItem>
                            {cursos.map((c) => (
                                <MenuItem key={c.idCurso} value={c.idCurso}>
                                    {c.titulo}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Tooltip title={!filtros.idCurso ? 'Selecciona un curso para habilitar grupos' : ''} arrow placement="top">
                        <span style={{ display: 'block', width: '100%' }}>
                            <FormControl fullWidth size="small" disabled={!filtros.idCurso}>
                                <InputLabel id="lbl-grupo">Grupo</InputLabel>
                                <Select labelId="lbl-grupo" label="Grupo" value={filtros.idGrupo} onChange={handleCambioGrupo}>
                                    <MenuItem value="">Todos</MenuItem>
                                    {grupos.map((g) => (
                                        <MenuItem key={g.idGrupo} value={g.idGrupo}>
                                            {g.nombre}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs>
                            <Button variant="contained" onClick={buscar} fullWidth>
                                Buscar
                            </Button>
                        </Grid>
                        {hayFiltrosSeleccionados && (
                            <Grid item>
                                <Tooltip title="Limpiar filtros">
                                    <IconButton onClick={limpiarFiltros} size="small">
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                {filtrosAplicados.length > 0 ? (
                    filtrosAplicados.map((f, idx) => <Chip key={idx} label={f.label} size="small" />)
                ) : (
                    <Chip icon={<InfoOutlined fontSize="small" />} label="Resultados sin filtros" size="small" variant="outlined" />
                )}
            </Stack>

            <Tabla
                rows={grupo.data}
                columns={headers().colaboradores}
                accionesTabla={acciones}
                accionesFila={accionesFila}
                columnKey="idcolaborador"
            />
            <ModalGeneral
                open={modal.open}
                titulo={modal.titulo}
                width="lg"
                acciones={
                    <Button onClick={cerrarModal} className={classes.btnCancelar}>
                        Cerrar
                    </Button>
                }
            >
                {modal.componente && <modal.componente {...modal.props} />}
            </ModalGeneral>
        </MainCard>
    );
};

export default Calificaciones;
